import { FC, ReactNode, useState, useEffect } from 'react';
import Modal from 'components/shared/Modal';
import { ModalRouteProps } from 'context/ModalRouter';
import Confetti from 'react-confetti';
import { useWindowSize } from 'utils/hooks/useWindowsSize';

const CONFETTI_DURATION_MS = 7000;
const CONFETTI_CONFIG = {
  numberOfPieces: 500,
  gravity: 0.3,
  zIndex: 9999,
} as const;

interface Props extends Partial<ModalRouteProps> {
  image: ReactNode;
  title: ReactNode;
  description?: ReactNode;
  button?: ReactNode;
  dataName?: string;
  dataCy?: string;
  open?: boolean;
  onClose?: () => void;
}

const CelebrationModalBase: FC<Props> = (props) => {
  const { width, height } = useWindowSize();
  const prefersReducedMotion =
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const [showConfetti, setShowConfetti] = useState(!prefersReducedMotion);

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowConfetti(false);
    }, CONFETTI_DURATION_MS);

    return () => clearTimeout(timer);
  }, []);

  return (
    <>
      {showConfetti && width !== null && height !== null && (
        <Confetti
          width={width}
          height={height}
          recycle={false}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            zIndex: CONFETTI_CONFIG.zIndex,
          }}
          numberOfPieces={CONFETTI_CONFIG.numberOfPieces}
          gravity={CONFETTI_CONFIG.gravity}
        />
      )}
      <Modal
        modalManager={props.modalManager}
        open={props.open}
        onClose={props.onClose}
        size="responsive"
        classNames={{
          modal: 'tw-w-auto md:!tw-w-[480px]',
        }}
        data-name={props.dataName}
        data-cy={props.dataCy}
      >
        <Modal.Header />
        <Modal.Content data-name={props.dataName}>
          {props.image}
          <div className="tw-my-4 tw-text-center">
            <p className="tw-mb-1 tw-text-lg tw-font-semibold">{props.title}</p>
            {props.description && (
              <p className="tw-text-md tw-font-regular tw-text-grayscale-140">
                {props.description}
              </p>
            )}
          </div>
        </Modal.Content>
        <Modal.Footer>{props.button}</Modal.Footer>
      </Modal>
    </>
  );
};

export default CelebrationModalBase;
